/*
 * Global date-picker auto-open
 * ------------------------------------------------------------------
 * Makes every <input type="date|datetime-local|time|month|week">
 * open its native picker when the user clicks or focuses anywhere
 * in the field, not just on the small calendar/clock icon.
 *
 * Uses event delegation at the document level so new fields rendered
 * later (modals, wizards, dynamic forms) are automatically covered —
 * no per-component changes required.
 *
 * showPicker() is supported in Chrome 99+, Edge 99+, Firefox 101+,
 * Safari 16+. On older browsers this silently falls back to default
 * browser behavior.
 */

const PICKER_TYPES = new Set(['date', 'datetime-local', 'time', 'month', 'week']);

function tryOpenPicker(el) {
  if (!el || !(el instanceof HTMLInputElement)) return;
  if (!PICKER_TYPES.has(el.type)) return;
  if (el.disabled || el.readOnly) return;
  if (typeof el.showPicker !== 'function') return;
  try {
    el.showPicker();
  } catch (err) {
    // showPicker() throws in a few known-benign situations:
    //   • called without user activation (browser gesture requirement)
    //   • called on a field whose type is being coerced at the moment
    //   • Safari < 16 (feature detection above should prevent this, but
    //     some embedded WebViews lie about typeof === 'function')
    // These are not user-facing errors, so we swallow them in prod and
    // only surface during development to avoid console noise.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[datePickerAutoOpen] showPicker() ignored:', err?.message || err);
    }
  }
}

function handleClick(e) {
  tryOpenPicker(e.target);
}

function handleFocus(e) {
  // Opening on focus covers keyboard-tab-into-field flows too.
  tryOpenPicker(e.target);
}

export function installDatePickerAutoOpen() {
  if (typeof document === 'undefined') return;
  if (document.__exrsimDatePickerAutoOpen) return;
  document.__exrsimDatePickerAutoOpen = true;
  document.addEventListener('click', handleClick, true);
  document.addEventListener('focusin', handleFocus, true);
}
